import {Slideshow, SlideshowEvents} from '@/slideshow'
import {Synchroniser, TimeListener} from '@/Synchroniser'
import {inject, injectable} from 'inversify'
import {ElementTimeline, SlideTimeline} from "./SlideTimeline"
import {KeyFrame} from "./KeyFrame"
import {SlideContentData, SlideElement, SlideElementType} from "@/Core/SlideshowData";
import {removeElement} from "@/Utility";
import {PubSub} from "@/Core/PubSub";
import {CreatorEvents} from "@/CreatorTools/Services/CreatorEvents";

@injectable()
export class AnimationPlugin implements TimeListener {

    constructor(
        @inject("Slideshow") private slideshow: Slideshow,
        @inject("PubSub") private pubSub: PubSub,
        @inject("Synchroniser")
        private sync: Synchroniser) {

        this.sync.registerTimeListener(this);

        this.pubSub.subscribeToEvent(SlideshowEvents.SlideExiting, () => {
            if (this.currentTimeline)
                this.currentTimeline.pause();
        });

        this.pubSub.subscribeToEvent(SlideshowEvents.SlideEntered, (slideData) => {
            this.currentTimeline = this.createAnimationForSlide(slideData);
            this.currentTimeline.seek(0);

            if (this.sync.isPlaying) {
                this.currentTimeline.play();
            }
            else {
                this.currentTimeline.pause();
            }

            return this.pubSub.publishEvent(AnimationEvents.Loaded, this.currentTimeline);
        });

        this.pubSub.subscribeToEvent(SlideshowEvents.Resize, () => {
            if (this.currentTimeline)
                return this.reloadTimelines();
        });

        this.pubSub.subscribeToEvent(CreatorEvents.AnimationUpdate, () => {
            console.log('x');
            if (this.currentTimeline)
                return this.reloadTimelines();
        });
    }

    currentTimeline: SlideTimeline;
    private _animationContainer: HTMLElement|null = null;

    get animationContainer() {
        return this._animationContainer;
    }

    set animationContainer(val) {
        this._animationContainer = val;
    }

    private createAnimationForSlide(slide: SlideContentData): SlideTimeline {
        let ets: ElementTimeline[] = [];

        for (let slideElement of slide.elements) {
            let html = document.getElementById(slideElement.id);
            if (html == null) {
                console.log("Could not find element with id: " + slideElement.id);
                continue;
            }

            // html.removeAttribute("style");
            html.style.transformOrigin = slideElement.origin;

            if (slideElement.type === SlideElementType.Text) {
                html.style.fontSize = slideElement.fontSize + "rem";
                if (slideElement.weight)
                    html.style.fontWeight = slideElement.weight.toString();
            }

            // if (slideElement.type === SlideElementType.Image) {
            //     if (slideElement.width) {
            //         slideElement.keyFrames[0].
            //     }
            // }

            // set an empty alt tag to images that should be ignored by screen readers
            if (html.tagName == "IMG" && html.getAttribute("alt") == null)
                html.setAttribute("alt", "");

            ets.push({
                element: html,
                keyFrames: slideElement.keyFrames,
                origin: slideElement.origin,
                type: slideElement.type
            });
        }

        return new SlideTimeline(this.sync, ets);
    }

    resized() {
        this.currentTimeline.reload();
    }

    findSlideElementById(id: string): SlideElement | undefined | null {
        let slide = this.slideshow.currentSlide;
        return slide.elements.find(e => e.id === id);
    }

    getSlideElementById(id: string): SlideElement {
        let slide = this.slideshow.currentSlide;
        let result = slide.elements.find(e => e.id === id);
        if (result == null)
            throw "Slide element not found";
        return result;
    }

    findKeyFrame(elementId: string, keyFrameIndex: number): KeyFrame | null | undefined {
        return this.findSlideElementById(elementId)?.keyFrames?.[keyFrameIndex];
    }

    getKeyFrame(elementId: string, keyFrameIndex: number): KeyFrame {
        let result = this.findSlideElementById(elementId)?.keyFrames?.[keyFrameIndex];
        if (result == null)
            throw "No keyframe exists";
        return result;
    }

    inBatch = false;

    startBatch() {
        this.inBatch = true;
    }

    endBatch() {
        this.inBatch = false;
        return this.reloadTimelines();
    }

    removeSlideElement(id: string) {
        let slide = this.slideshow.currentSlide;
        let i = slide.elements.findIndex(e => e.id === id);

        if (i !== -1) {
            slide.elements.splice(i, 1);
            removeElement(document.getElementById(id));
            if (this.inBatch)
                return Promise.resolve();
            else
                return this.reloadTimelines();
        }
    }

    // selectElements(elementIds: string[], createKeyFrames: boolean): Promise<any> {
    //     let seconds = this.sync.getSlideProgress();
    //     let selectedKeyFrames: ISelectedKeyFrame[] = [];
    //     let promises = [];
    //
    //     for (let id of elementIds) {
    //         let el = this.getSlideElement(id);
    //         if (el == null)
    //             continue;
    //
    //         let i = el.keyframes.findIndex(k => k.seconds === seconds);
    //         if (i >= 0) {
    //             selectedKeyFrames.push({
    //                 elementId: id,
    //                 keyFrameIndex: i
    //             });
    //         }
    //         else if (createKeyFrames) {
    //             let promise = this.insertKeyFrame(id, {
    //                 seconds: seconds
    //             }).then((index) => selectedKeyFrames.push({
    //                 elementId: id,
    //                 keyFrameIndex: index
    //             }));
    //
    //             promises.push(promise);
    //         }
    //     }
    //
    //     return Promise.all(promises);
    //         .then(() => {
    //             if (selectedKeyFrames.length > 0)
    //                 return this.publishSelectKeyFrames(selectedKeyFrames);
    //             else
    //                 return Promise.resolve();
    //         });
    // }

    reloadTimelines() {
        if (this.currentTimeline) {
            this.currentTimeline.destroy();
        }

        this.currentTimeline = this.createAnimationForSlide(this.slideshow.currentSlide);
        this.currentTimeline.seek(this.sync.getSlideProgress());
        return this.pubSub.publishEvent(AnimationEvents.Loaded, this.currentTimeline);
    }

    stopping() {
        this.currentTimeline?.pause();
        this.currentTimeline?.seek(0);
    }

    playing(): Promise<void> {
        this.currentTimeline?.play();
        return Promise.resolve();
    }

    pausing() {
        this.currentTimeline?.pause();
    }
    
    seek(time: number) {
        this.currentTimeline?.seek(time);
    }

    endTime(): number {
        return 0;
        /*
        let slideId = this.getSlideId(this.slideshow.getCurrentSlide());
        let timeline = this.timelines[slideId];
        return timeline.endTime(true);
        */
    }
}

export enum AnimationEvents {
    Loaded = "AnimationsLoaded"
}

export interface ISelectedKeyFrame {
    elementId: string;
    keyFrameIndex: number;
}